import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import AIOSEmphasisSlide from "../components/slides/AIOSEmphasisSlide";
import TotalCostCalculatorSlide from "../components/slides/TotalCostCalculatorSlide";
import AIFunctionsSlide from "../components/slides/AIFunctionsSlide";
import AIFunctionsValueSlide from "../components/slides/AIFunctionsValueSlide";
import AIFunctionsAllValueSlide from "../components/slides/AIFunctionsAllValueSlide";
import QuoteEngagementModelsSlide from "../components/quote/QuoteEngagementModelsSlide";
import QuoteROISlide from "../components/quote/QuoteROISlide";
import QuoteYourPricingSlide from "../components/quote/QuoteYourPricingSlide";
import QuoteContractTermsSlide from "../components/quote/QuoteContractTermsSlide";
import QuoteClosingSlide from "../components/quote/QuoteClosingSlide";
import QuoteTitleSlide from "../components/quote/QuoteTitleSlide";
import EmailGate from "../components/EmailGate";

const QuotePage = () => {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gateUnlocked, setGateUnlocked] = useState(false);

  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);

  // Fetch quote data
  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const isLocalhost =
          typeof window !== "undefined" &&
          (window.location.hostname === "localhost" ||
            window.location.hostname === "127.0.0.1");
        const apiUrl =
          process.env.REACT_APP_API_URL ||
          (isLocalhost ? "http://localhost:8001" : "");

        const response = await fetch(`${apiUrl}/api/quote/${id}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || "Quote not found");
        }

        setQuote(data);
      } catch (err) {
        setError(err.message || "Failed to load quote");
      } finally {
        setLoading(false);
      }
    };

    fetchQuote();
  }, [id]);

  // Scale slides to fit viewport
  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const parentWidth = containerRef.current.offsetWidth;
        const targetWidth = 1400;
        const newScale = parentWidth / targetWidth;
        setScale(newScale);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Carousel initialization
  useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (!api) return;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        api.scrollNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        api.scrollPrev();
      }
    },
    [api]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-neutral-400 text-sm">Loading quote...</p>
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">!</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Quote Not Found</h2>
          <p className="text-neutral-400 text-sm">
            {error || "This quote link may be invalid or expired."}
          </p>
        </div>
      </div>
    );
  }

  if (!gateUnlocked) {
    return (
      <EmailGate
        onAccessGranted={() => setGateUnlocked(true)}
        contentId={`quote-${id}`}
        contentType="quote"
        contentTitle={`Quote for ${quote.practiceName}`}
      />
    );
  }

  const slideLabels = [
    "",
    "The Asha AI OS",
    "Piecemeal Cost",
    "What Staff Does",
    "The Real Cost",
    "All For <$15",
    "Pricing",
    "Perspective",
    "ROI Potential",
    "Why Asha",
    "Contract Terms",
  ];

  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xs">A</span>
          </div>
          <div>
            <span className="text-white text-sm font-semibold">Asha</span>
            <span className="text-neutral-500 text-sm ml-2">|</span>
            <span className="text-neutral-400 text-sm ml-2">
              Quote for {quote.practiceName}
            </span>
          </div>
        </div>
        <div className="text-neutral-500 text-xs">
          {current + 1} / {count || 11}
        </div>
      </div>

      {/* Main slide area */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8">
        <div className="w-full max-w-[1400px] aspect-video relative group">
          <div
            ref={containerRef}
            className="w-full h-full overflow-hidden bg-transparent relative"
          >
            <div
              style={{
                width: "1400px",
                height: "787.5px",
                transform: `scale(${scale})`,
                transformOrigin: "top left",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            >
              <Carousel setApi={setApi} className="w-full h-full">
                <div className="w-full h-full bg-white rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10 relative isolate transform-gpu">
                  <CarouselContent className="h-full ml-0">
                    {/* Slide 0: Title */}
                    <CarouselItem className="h-full pl-0">
                      <QuoteTitleSlide
                        practiceName={quote.practiceName}
                        practiceLogo={quote.practiceLogo}
                      />
                    </CarouselItem>
                    {/* Slide 1: Asha AI OS (from /os-v2) */}
                    <CarouselItem className="h-full pl-0">
                      <AIOSEmphasisSlide quoteMode />
                    </CarouselItem>
                    {/* Slide 2: Total Cost to Piecemeal (from /os-v2) */}
                    <CarouselItem className="h-full pl-0">
                      <TotalCostCalculatorSlide
                        initialCollections={quote.monthlyCollections}
                        initialPatients={quote.monthlyPatients}
                        initialProviders={quote.providers}
                        quoteMode
                      />
                    </CarouselItem>
                    {/* Slide 3: What Your Staff Does (from /os-v2) */}
                    <CarouselItem className="h-full pl-0">
                      <AIFunctionsSlide hideTag />
                    </CarouselItem>
                    {/* Slide 4: The Real Cost (from /os-v2) */}
                    <CarouselItem className="h-full pl-0">
                      <AIFunctionsValueSlide />
                    </CarouselItem>
                    {/* Slide 5: All tasks for <$15 */}
                    <CarouselItem className="h-full pl-0">
                      <AIFunctionsAllValueSlide />
                    </CarouselItem>
                    {/* Slide 6: Pricing tiers with auto-selected tier (from /os-v2) */}
                    <CarouselItem className="h-full pl-0">
                      <QuoteEngagementModelsSlide
                        patients={quote.monthlyPatients}
                        planType={quote.planType}
                        fixedPrice={quote.fixedPrice}
                        fixedPriceMaxCollections={quote.fixedPriceMaxCollections}
                        fixedPriceMaxPatients={quote.fixedPriceMaxPatients}
                        waiveSoftwareFeeFirst12Months={quote.waiveSoftwareFeeFirst12Months}
                        softwareFreeAfter12Months={quote.softwareFreeAfter12Months}
                        collections={quote.monthlyCollections}
                        ashaPrice={quote.ashaPrice}
                        ashaPriceLabel={quote.ashaPriceLabel}
                      />
                    </CarouselItem>
                    {/* Slide 6: Perspective */}
                    <CarouselItem className="h-full pl-0">
                      <QuoteYourPricingSlide
                        practiceName={quote.practiceName}
                        collections={quote.monthlyCollections}
                        patients={quote.monthlyPatients}
                        providers={quote.providers}
                        planType={quote.planType}
                        fixedPrice={quote.fixedPrice}
                        fixedPriceMaxCollections={quote.fixedPriceMaxCollections}
                        fixedPriceMaxPatients={quote.fixedPriceMaxPatients}
                        waiveSoftwareFeeFirst12Months={quote.waiveSoftwareFeeFirst12Months}
                        softwareFreeAfter12Months={quote.softwareFreeAfter12Months}
                        pricingPerspective={quote.pricingPerspective}
                        ashaPrice={quote.ashaPrice}
                        ashaPriceLabel={quote.ashaPriceLabel}
                      />
                    </CarouselItem>
                    {/* Slide 7: ROI Potential */}
                    <CarouselItem className="h-full pl-0">
                      <QuoteROISlide
                        collections={quote.monthlyCollections}
                        patients={quote.monthlyPatients}
                        practiceName={quote.practiceName}
                        roiRows={quote.roiRows}
                        roiTotalConservative={quote.roiTotalConservative}
                        roiTotalRealistic={quote.roiTotalRealistic}
                      />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0">
                      <QuoteClosingSlide
                        practiceName={quote.practiceName}
                        collections={quote.monthlyCollections}
                        patients={quote.monthlyPatients}
                        providers={quote.providers}
                        ashaPrice={quote.ashaPrice}
                        planType={quote.planType}
                      />
                    </CarouselItem>
                    <CarouselItem className="h-full pl-0">
                      <QuoteContractTermsSlide
                        planType={quote.planType}
                      />
                    </CarouselItem>
                  </CarouselContent>
                </div>
              </Carousel>
            </div>
          </div>

          {/* Navigation buttons */}
          <Button
            variant="outline"
            size="icon"
            className="absolute -left-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 border-0 text-white hover:text-white h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
            onClick={() => api?.scrollPrev()}
            disabled={!api || current <= 0}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>

          <Button
            variant="outline"
            size="icon"
            className="absolute -right-6 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 border-0 text-white hover:text-white h-12 w-12 rounded-full backdrop-blur-sm shadow-sm z-50 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-0"
            onClick={() => api?.scrollNext()}
            disabled={!api || current >= (count - 1)}
          >
            <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Bottom navigation */}
      <div className="flex items-center justify-center gap-2 pb-6">
        {slideLabels.map((label, i) => (
          <button
            key={i}
            onClick={() => api?.scrollTo(i)}
            className={`rounded-full text-xs font-medium transition-all ${
              label
                ? `px-3 py-1.5 ${current === i ? "bg-blue-600 text-white" : "bg-white/5 text-neutral-400 hover:bg-white/10 hover:text-neutral-300"}`
                : `w-2 h-2 ${current === i ? "bg-blue-600" : "bg-white/20 hover:bg-white/30"}`
            }`}
          >
            {label || ""}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuotePage;
